import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { App } from './App';
import { AUTH_API_CONFIG } from './config/api.config';
import { initSharedAuth } from './auth/sharedAuth';
import { initializeMsalAuth } from './services/authInitializer.service';
import { msalReady } from './services/msalBootstrap.service';
import { setupApiInterceptor } from './services/api.service';
import { initializeApplicationInsights } from './services/applicationInsights.service';
import { shouldUseMsalRedirect } from './utils/featureFlags.utils';
import { cleanupRedirectState } from './utils/redirectTransaction.utils';
// Fontsource variable font — documented entry imports (weight + italic axes):
// https://github.com/fontsource/fontsource/blob/main/website/docs/getting-started/variable.mdx
import '@fontsource-variable/zalando-sans/wght.css';
import '@fontsource-variable/zalando-sans/wght-italic.css';
import './index.css';

// Run before app so token from host portal redirect (URL hash) is available when AuthContext initializes
initSharedAuth();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

type WindowWithReactRoot = Window & { __reactRoot?: ReactDOM.Root };
const windowWithRoot = window as WindowWithReactRoot;
let root: ReactDOM.Root;
if (!windowWithRoot.__reactRoot) {
  root = ReactDOM.createRoot(rootElement);
  windowWithRoot.__reactRoot = root;
} else {
  root = windowWithRoot.__reactRoot;
}

(async () => {
  if (import.meta.env.PROD) {
    try {
      initializeApplicationInsights();
    } catch (error) {
      console.warn('[Main] Failed to initialize Application Insights:', error);
    }
  }

  // Always register API interceptors so shared-token flows (URL/postMessage handoff)
  // can add Bearer headers even when providerType is not msal.
  setupApiInterceptor();
  if (AUTH_API_CONFIG.providerType === 'msal') {
    const useRedirect = shouldUseMsalRedirect();
    if (!useRedirect) {
      cleanupRedirectState();
    }
    try {
      const instance = await msalReady;
      if (instance) {
        initializeMsalAuth(instance, useRedirect ? InteractionType.Redirect : InteractionType.Popup);
        const AppWrapper = import.meta.env.DEV ? React.Fragment : React.StrictMode;
        root.render(
          <AppWrapper>
            <MsalProvider instance={instance}>
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <App />
              </BrowserRouter>
            </MsalProvider>
          </AppWrapper>
        );
      } else {
        root.render(
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <App />
          </BrowserRouter>
        );
      }
    } catch (error) {
      console.error('[MSAL] Bootstrap error:', error);
      root.render(
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <App />
        </BrowserRouter>
      );
    }
  } else {
    root.render(
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <App />
      </BrowserRouter>
    );
  }
})();
